import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BackEnd } from '@helpers/BackEnd.class';
import { KTResponse } from '@interfaces/Generic/KTResponse.interface';
import { AddPaymentParams } from '@interfaces/KT/WorldPay/AddPayment.interface';
import { BookingPaymentInitiationParams, GroupPaymentInitiationParams, PaymentInitiationResponse } from '@interfaces/KT/WorldPay/PaymentInitiation.interface';
import { SetPaymentErrorParams } from '@interfaces/KT/WorldPay/SetPaymentError.interface';
import { LoadingService } from '@services/loading.service';
import { finalize, lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BookingFlowServicesService {
  private readonly _http = inject(HttpClient);
  private readonly _loadingService = inject(LoadingService);

  private readonly BookingFlowServicesPath = '_vti_bin/CostaClickNew/UIServices/BookingFlowServices.svc/';

  private get CCKBasePath() {
    return window?.cckBasePath;
  }

  /**
   * Inizializza un pagamento per i booking con WordPay e torna la URL per la pagina di inserimento dei dati della carta ci credito.
   * 
   * Agli URL di Success, Error ed Abort, lato BE, viene poi accordato l'orderCode necessario per le successive chiamate di AddPayment e SetPaymentError.
   * 
   * @param { BookingPaymentInitiationParams } params parametri da passare al BE
   * @returns { Promise<PaymentInitiationResponse> }
   */
  BookingPaymentInitiation(params: BookingPaymentInitiationParams) {
    return this.PaymentInitiation(params);
  }

  /**
   * Inizializza un pagamento per i gruppi  con WordPay e torna la URL per la pagina di inserimento dei dati della carta ci credito.
   * 
   * Agli URL di Success, Error ed Abort, lato BE, viene poi accordato l'orderCode necessario per le successive chiamate di AddPayment e SetPaymentError.
   * 
   * @param { GroupPaymentInitiationParams } params parametri da passare al BE
   * @returns { Promise<PaymentInitiationResponse> }
   */
  GroupPaymentInitiation(params: GroupPaymentInitiationParams) {
    return this.PaymentInitiation(params);
  }

  private PaymentInitiation(request: GroupPaymentInitiationParams | BookingPaymentInitiationParams): Promise<PaymentInitiationResponse> {
    const api = `${this.CCKBasePath}${this.BookingFlowServicesPath}PaymentInitiation`;
    const loader = 'PaymentInitiation';
    this._loadingService.startLoading(loader);
    const obs$ = this._http
      .post<KTResponse<PaymentInitiationResponse>>(
        api,
        {
          request,
        },
        BackEnd.WithCredentials
      )
      .pipe(
        BackEnd.HandleKtApi,
        finalize(() => this._loadingService.stopLoading(loader))
      );

    return lastValueFrom(obs$);
  }

  /**
   * Registra l'avvenuto pagamento su TTG
   * 
   * @param { AddPaymentParams } params parametri da passare al BE
   * @returns { Promise<null> }
   */
  AddPayment(params: AddPaymentParams): Promise<null> {
    const api = `${this.CCKBasePath}${this.BookingFlowServicesPath}AddPayment`;
    const loader = 'AddPayment';
    this._loadingService.startLoading(loader);
    const obs$ = this._http
      .post<KTResponse<null>>(
        api,
        params,
        BackEnd.WithCredentials
      )
      .pipe(
        BackEnd.HandleKtApi,
        finalize(() => this._loadingService.stopLoading(loader))
      );

    return lastValueFrom(obs$);
  }

  /**
   * Registra l'errore del pagamento su TTG
   * 
   * @param { SetPaymentErrorParams } params parametri da passare al BE
   * @returns { Promise<null> }
   */
  SetPaymentError(params: SetPaymentErrorParams): Promise<null> {
    const api = `${this.CCKBasePath}${this.BookingFlowServicesPath}SetPaymentError`;
    const loader = 'SetPaymentError';
    this._loadingService.startLoading(loader);
    const obs$ = this._http
      .post<KTResponse<null>>(
        api,
        params,
        BackEnd.WithCredentials
      )
      .pipe(
        BackEnd.HandleKtApi,
        finalize(() => this._loadingService.stopLoading(loader))
      );

    return lastValueFrom(obs$);
  }
}
